import { clsx } from 'clsx';
import { type FormEvent, useMemo, useState } from 'react';
import type { PlayerListItem } from '#shared/schemas/player';
import { fill } from '@/copy';
import { toast } from '@/lib/toast';
import { Button, Tag, TextField } from '@/ui';
import { PlayerAvatar } from '../cards/public';
import { useRatingCards } from '../stats/public';
import { errorText } from './components/errors';
import { copy } from './copy';
import { useCreateGuest, usePlayers, wasQueued } from './queries';
import { matchPlayer } from './search';

/**
 * FROZEN props for the picker every setup screen uses (games, tournaments, Game Lab, sessions).
 * Two columns of 56 px tiles: face, name, and the pick order once chosen. `presentFirst` puts those
 * players on top with a "Here" tag (sessions' useHereToday). `allowGuest` shows the "add a guest"
 * row, which creates a guest and selects it (with no signal the guest waits on the phone and still
 * shows up here). A search box appears once the crew is bigger than a pickup game.
 */
export interface PlayerPickerProps {
  selected: readonly string[];
  onChange: (ids: string[]) => void;
  /** Most players that can be picked. Further taps are ignored. */
  max?: number;
  /** Ids to leave out of the list. */
  exclude?: readonly string[];
  presentFirst?: readonly string[];
  allowGuest?: boolean;
}

const SEARCH_FROM = 9;

/** Players in picker order: leave out `exclude`, put `first` on top, keep the roster order inside each group. */
export function pickerRows(
  players: readonly PlayerListItem[],
  { exclude, first, query }: { exclude: ReadonlySet<string>; first: ReadonlySet<string>; query: string },
): PlayerListItem[] {
  return players
    .filter((p) => !exclude.has(p.id) && matchPlayer(p, query))
    .map((p, i) => ({ p, i }))
    .sort((a, b) => Number(first.has(b.p.id)) - Number(first.has(a.p.id)) || a.i - b.i)
    .map((x) => x.p);
}

export function PlayerPicker({ selected, onChange, max, exclude, presentFirst, allowGuest }: PlayerPickerProps) {
  const { players, isLoading } = usePlayers();
  const { byId } = useRatingCards();
  const create = useCreateGuest();
  const [guest, setGuest] = useState('');
  const [query, setQuery] = useState('');
  const skip = useMemo(() => new Set(exclude ?? []), [exclude]);
  const first = useMemo(() => new Set(presentFirst ?? []), [presentFirst]);
  const rows = useMemo(() => pickerRows(players, { exclude: skip, first, query }), [players, skip, first, query]);
  const full = max !== undefined && selected.length >= max;
  const searchable = players.length - skip.size >= SEARCH_FROM;

  const toggle = (id: string) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else if (max === undefined || selected.length < max) onChange([...selected, id]);
  };
  const addGuest = async (e: FormEvent) => {
    e.preventDefault();
    const name = guest.trim();
    if (!name) return;
    try {
      const id = await create.mutateAsync(name);
      setGuest('');
      if (full) toast({ title: copy.guest.full });
      else {
        toggle(id);
        if (wasQueued(id)) toast({ title: fill(copy.picker.guestQueued, { name }) });
      }
    } catch (err) {
      toast({ title: errorText(err, copy.guest.failed), tone: 'danger' });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {searchable && (
        <TextField
          label={copy.picker.searchLabel}
          hideLabel
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={copy.picker.searchPlaceholder}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
      )}

      {rows.length === 0 ? (
        <p className="tts-card-pale p-3 text-sm font-semibold">
          {isLoading ? copy.picker.loading : query ? copy.picker.noMatch : copy.picker.empty}
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-2">
          {rows.map((p) => {
            const at = selected.indexOf(p.id);
            const on = at >= 0;
            const blocked = full && !on;
            return (
              <li key={p.id}>
                <button
                  type="button"
                  aria-pressed={on}
                  aria-disabled={blocked || undefined}
                  onClick={() => toggle(p.id)}
                  className={clsx(
                    'tts-press flex min-h-14 w-full items-center gap-2 rounded-tts border-(length:--tts-border) border-ink px-2 py-1.5 text-left text-ink shadow-btn',
                    on ? 'bg-mint' : 'bg-white',
                    blocked && 'opacity-55',
                  )}
                >
                  <PlayerAvatar player={p} rating={byId.get(p.id)} size={40} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-bold leading-tight">{p.displayName}</span>
                    <span className="flex gap-1">
                      {first.has(p.id) && <Tag tone="butter">{copy.picker.here}</Tag>}
                      {p.memberId === null && !first.has(p.id) && (
                        <span className="text-xs font-semibold">{copy.picker.guestTag}</span>
                      )}
                    </span>
                  </span>
                  {on && (
                    <span
                      role="img"
                      aria-label={fill(copy.picker.pickOrder, { n: at + 1 })}
                      className="tts-display grid size-7 shrink-0 place-items-center rounded-chip border-2 border-ink bg-white text-sm"
                    >
                      {at + 1}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {max !== undefined && (
        <p className="text-sm font-semibold" aria-live="polite">
          {full ? fill(copy.picker.max, { n: max }) : fill(copy.picker.selected, { n: selected.length })}
        </p>
      )}

      {allowGuest && (
        <form onSubmit={addGuest} className="flex items-end gap-2">
          <TextField
            className="min-w-0 flex-1"
            label={copy.picker.guestLabel}
            hideLabel
            value={guest}
            onChange={(e) => setGuest(e.target.value)}
            placeholder={copy.picker.guestPlaceholder}
            maxLength={40}
            autoComplete="off"
            autoCapitalize="words"
            spellCheck={false}
          />
          <Button type="submit" icon="plus" loading={create.isPending} disabled={guest.trim() === ''}>
            {copy.picker.guestAdd}
          </Button>
        </form>
      )}
    </div>
  );
}
